import i18n from 'i18next';
import { useLocationStore } from '../store/useLocationStore';
import { notify } from './notify';
import { getLocaleById } from './localeService';
import { logDebug } from './loggerService';

// In-flight/applied guard — once a migration has been applied for
// (oldLocaleId → newLocaleId), skip re-applying it. Handles:
//   - WS at-least-once replay for the same event
//   - WS event + feed-response marker racing (both fire for the same
//     migration; whichever lands first wins, the other is a no-op)
//   - Multiple feed responses (list + map) both carrying the marker
// Set membership is `oldLocaleId → newLocaleId`; both must match, so a
// hypothetical re-migration (locale A migrated twice) still runs the
// second time.
const applied = new Set<string>();
const inFlight = new Set<string>();

/**
 * Reconcile FE state to a locale migration reported either by the
 * `locale_migrated` WS event or by a `localeMigrated` field on a
 * `/listings/local/search` response.
 *
 * Guards:
 *   1. Current localeId in the store must equal `oldLocaleId` — otherwise
 *      the user has already moved off (via TopBar / earlier reconcile).
 *   2. Never re-apply the same (old → new) pair, or run two in flight.
 *
 * On apply:
 *   - fetch new locale by id
 *   - `useLocationStore.setLocale(newLocale)` — same code path the TopBar
 *     switcher uses; the store change triggers wsService's localeId watcher
 *     to unsubscribe(old) + subscribe(new) automatically
 *   - toast `location:migration.renamedToast`
 *   - subsequent `useLocalListings` requests key off the new localeId
 *     naturally — no manual queryClient plumbing needed
 *
 * Fire-and-forget. Errors are logged but not rethrown — the DB / BE state
 * is authoritative; a failed toast doesn't make the user any worse off
 * than a missed WS event, which is the whole reason we have this fallback.
 */
export async function applyLocaleMigration(
  oldLocaleId: string,
  newLocaleId: string,
): Promise<void> {
  if (!oldLocaleId || !newLocaleId || oldLocaleId === newLocaleId) return;

  const key = `${oldLocaleId}→${newLocaleId}`;
  if (applied.has(key) || inFlight.has(key)) return;

  const current = useLocationStore.getState().localeId;
  if (current !== oldLocaleId) return;

  inFlight.add(key);
  try {
    const newLocale = await getLocaleById(newLocaleId);
    // Re-check the store — a concurrent user action (TopBar swap) may have
    // moved them off the old locale while we were fetching. Don't clobber.
    if (useLocationStore.getState().localeId !== oldLocaleId) return;

    useLocationStore.getState().setLocale(newLocale);
    applied.add(key);

    const name = newLocale.name || '';
    if (name) {
      notify.info(
        i18n.t('migration.renamedToast', { ns: 'location', name }),
        { id: `locale-migrated:${newLocaleId}` },
      );
    }
  } catch (err) {
    logDebug('applyLocaleMigration failed', undefined, { oldLocaleId, newLocaleId, err });
  } finally {
    inFlight.delete(key);
  }
}
